import { useEffect, useRef, useState } from 'react';
import { useFetchTrigger } from './useFetchTrigger';

// Phase of a resource fetch. `data` carries the seed (optimistic) or the last
// hydrated value, so a seeded resource is `ready` on first paint and never blanks.
export type ResourcePhase = 'loading' | 'ready' | 'error';
export interface ResourceState<T> {
  phase: ResourcePhase;
  data: T | null; // seed, then hydrated value; held across refetches
  hydrated: boolean; // true once a live fetch has returned (seed alone = false)
}

export interface UseResourceOpts<T> {
  // Optimistic initial value (e.g. mapped from a list row). When present the
  // resource is `ready` immediately and the fetch hydrates in the background.
  seed?: T | null;
  // Gate the fetch (default true). Disabled → no request; seed still shows.
  enabled?: boolean;
  // Mutation tags (data domains) that revalidate this resource. Default
  // (undefined) → the wildcard, i.e. any write — the original behaviour.
  // `['account']` → only account writes. `[]` → never on writes (activation only),
  // for data that isn't write-signalled. See lib/mutationBus.ts.
  invalidatedBy?: string[];
}

// The SWR-with-seed engine every modal/drawer composes (DRY). Built on the one
// trigger core (lib/useFetchTrigger — the same core the page data hooks use):
// seed paints instantly, the fetch hydrates, a post-write reload() refetches,
// on-return revalidates silently, and the last-known value is held across
// refetches — never a blank.
export function useResource<T>(
  // Identity of the resource (e.g. `booking:b123`). null disables the fetch.
  key: string | null,
  fetcher: () => Promise<T>,
  opts: UseResourceOpts<T> = {},
): { state: ResourceState<T>; reload: () => void } {
  const { seed = null, enabled = true, invalidatedBy } = opts;
  const [data, setData] = useState<T | null>(seed);
  const [phase, setPhase] = useState<ResourcePhase>(seed != null ? 'ready' : 'loading');
  const [hydrated, setHydrated] = useState(false);
  const trigger = useFetchTrigger({ invalidatedBy, seeded: seed != null });

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!enabled || key == null) return;
    const run = trigger.begin();
    if (run.plan.showLoading && !run.hasContent) setPhase('loading');
    fetcherRef.current()
      .then((d) => {
        if (!run.isCurrent()) return; // a newer fetch superseded this one
        run.markContent();
        setData(d);
        setHydrated(true);
        setPhase('ready');
      })
      .catch(() => {
        if (!run.isCurrent()) return;
        // Hold the last-known value on a failed silent revalidation; otherwise error.
        if (!(run.isRevalidate && run.hasContent)) setPhase(run.hasContent ? 'ready' : 'error');
      });
    return run.cancel;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, trigger.nonce, trigger.tick, enabled]);

  return { state: { phase, data, hydrated }, reload: trigger.reload };
}
